import React from "react"
import { useOutletContext } from "react-router-dom"
import { Space, Typography } from "antd"

import { W2 } from "@components"
import Identity from "@components/Identity"
import { EditButton } from "@components/Button"
import { useAppContext } from "@components/AppContext"
import { isConsoleUser } from "@components/Authorization"
import { Markdown, Value } from "@components/Text"
import { TRANSACTION_TYPES } from "@components/Domain"
import { computeStatus, getTransactionStatusLabel, getTransactionTypeLabel } from "@components/Transaction"

import parseStatusReason from "./parseStatusReason"

const { Text } = Typography

const LABEL_ON = "on"
const LABEL_ID = "Correlation ID"
const LABEL_TO = "To"
const LABEL_FROM = "From"
const LABEL_TYPE = "Type"
const LABEL_DATE = "Date"
const LABEL_AMOUNT = "Amount"
const LABEL_STATUS = "Status"
const LABEL_CREATED = "Created"
const LABEL_UPDATED = "Last Updated"
const LABEL_RETRIED = "Retried"
const LABEL_BACKFILL = "Backfill"
const LABEL_TEMPLATE = "Template"


const useItems = (organizationName, transaction, onEdit) => {
  const { getOrganizationId } = useAppContext()
  const { isInvalidBankAccountTransaction } = useOutletContext()

  const {
    id,
    type,
    date,
    amount,
    status,
    details,
    createdAt,
    updatedAt,
    createdBy,
    isExternal,
    holdReason,
    description,
    transferUrl,
    statusReason,
    templateName,
    templateDate,
    senderSourceName = "...",
    receiverSourceName = "N/A",
    retryTransactionId,
  } = transaction

  const isHold = status === "HOLD"

  const hasDwollaUrl = !isExternal && !!transferUrl && isConsoleUser()

  const isRejected =
    status === "REJECTED" ||
    status === "ERROR" ||
    status === "FAILED"

  const isFundingTransaction = type === TRANSACTION_TYPES.FUNDING

  const isInvalidBankAccount = isInvalidBankAccountTransaction(transaction)

  const parsedStatusReason = parseStatusReason({
    statusReason,
    organizationName,
    isFundingTransaction,
    isInvalidBankAccount,
  })

  const components = {
    p(props) {
      const { children } = props

      return (
        <Typography.Paragraph>
          {children}
        </Typography.Paragraph>
      )
    },
    ul(props) {
      const { children } = props

      return (
        <ul style={{ margin: 0, paddingLeft: W2 }}>
          {children}
        </ul>
      )
    }
  }

  const getItem = (isCondition, props, altProps) =>
    // eslint-disable-next-line no-nested-ternary
    isCondition
      ? [ props ]
      : altProps ? [altProps] : []

  const typeValue = getTransactionTypeLabel(type)

  const computedStatus = computeStatus(transaction)
  const statusLabel = getTransactionStatusLabel(computedStatus)

  const getUrl = () => transferUrl.replace('://api', '://dashboard')

  const items = [
    {
      label: LABEL_ID,
      value: (
        <Space>
          {
            hasDwollaUrl
              ? (
                  <Typography.Link href={getUrl()} target="_blank">
                    {getOrganizationId()}-{id}
                  </Typography.Link>
                )
              : `${getOrganizationId()}-${id}`
          }
        </Space>
      ),
    },
    {
      label: LABEL_TYPE,
      value: isExternal
        ? `${typeValue} (${LABEL_BACKFILL})`
        : typeValue
    },
    {
      label: LABEL_DATE,
      value: <Value value={date} />
    },
    {
      label: LABEL_AMOUNT,
      value: (
        <Space>
          <Value value={amount} />
          { onEdit && <EditButton onClick={onEdit} /> }
        </Space>
      )
    },
    ...getItem(!isExternal,
      {
        label: LABEL_STATUS,
        value: !!retryTransactionId
          ? `${statusLabel} (${LABEL_RETRIED})`
          : statusLabel,
      }
    ),
    ...getItem(isHold,
      {
        label: "Hold Reason",
        value: holdReason
      }
    ),
    ...getItem(isRejected,
      {
        label: "Status Reason",
        value: <Markdown text={parsedStatusReason} components={components} />
      }
    ),
    ...getItem(isExternal,
      {
        label: "Description",
        value: description || "N/A",
      },
      {
        label: "ACH Details",
        value: details || "N/A",
      }
    ),
  ]

  if (!isExternal) {
    items.push(
      {
        label: LABEL_FROM,
        value: senderSourceName,
      },
      {
        label: LABEL_TO,
        value: receiverSourceName,
      },
    )
  }

  items.push(
    {
      label: LABEL_TEMPLATE,
      value: (
        templateName
          ? <Space>{templateName} - <Value value={templateDate} /></Space>
          : "N/A"
      ),
    },
    {
      label: LABEL_CREATED,
      value: (
        <Space size={6}>
          <Identity id={createdBy} />

          {createdAt && <Text type="secondary">{LABEL_ON}</Text> }
          {createdAt && <Value value={createdAt} />}
        </Space>
      )
    },
    {
      label: LABEL_UPDATED,
      value: <Value value={updatedAt} />
    },
  )

  return items
}

export default useItems
